import { useLocalRenderDestinations } from './use-local-render-destinations';
import { useCommerce } from '../account/commerce';
import { API, apiRequest, jsonPost } from '../services/api-client';
import { useEffect, useRef, useState } from 'react';
import { cancelVideo, createVideo, listVideos, localCapabilities, type VideoJob, type VideoRequest } from '../services/local-video';
import { cloudAction, cloudVideos, cloudVideo, submitCloudVideo, session } from '../services/cloud-video';
import { ApiError } from '../services/api-client';
import { signInToLocalPreview } from '../services/local-preview-auth';

/** Retrieves durable cloud jobs or the existing local renderer without keeping the drawer open. */
export function useVideoExports(enabled: boolean) {
  const commerce = useCommerce();
  const [filter, setFilter] = useState('');
  const destinations = useLocalRenderDestinations(enabled, filter);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [mode, setMode] = useState<'local' | 'cloud' | 'alpha' | null>(null);
  const [socialLayoutsSupported, setSocialLayoutsSupported] = useState(false);
  const [sharedVideos, setSharedVideos] = useState(false);
  const [operator, setOperator] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [localSignIn, setLocalSignIn] = useState(false);
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const loadedOlder = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [connectionError, setConnectionError] = useState('');
  useEffect(() => {
    if (!enabled || destinations.active) return;
    setJobs([]); setNextCursor(null); loadedOlder.current = false;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    let local = false;
    let hosted = false;
    const reconnect = () => { if (active) timer = setTimeout(() => void connect(), 3000); };
    const poll = async () => {
      try {
        const page = local ? { jobs: await listVideos(), nextCursor: null } : await cloudVideos(undefined, filter);
        if (active) {
          setJobs(current => [...page.jobs, ...(page.nextCursor ? current.filter(job => !page.jobs.some(fresh => fresh.id === job.id)) : [])]);
          if (!page.nextCursor) loadedOlder.current = false;
          if (!loadedOlder.current) setNextCursor(page.nextCursor);
          setConnectionError('');
        }
      } catch (cause) {
        if (active) {
          setConnectionError(cause instanceof Error ? cause.message : 'Unable to refresh video status.');
          setAvailable(false);
          setAuthRequired(cause instanceof ApiError && cause.status === 401);
          reconnect();
        }
        return;
      }
      if (active) timer = setTimeout(() => void poll(), local && !hosted ? 1000 : 5000);
    };
    const connect = async () => {
      try {
        const capabilities = await localCapabilities();
        local = capabilities.localRendering;
        hosted = capabilities.hostedAlpha === true;
        if (!active) return;
        setLocalSignIn(capabilities.localSignIn);
        setSocialLayoutsSupported(capabilities.presentationVersions?.includes(1) === true && capabilities.presentationVersions.includes(2));
        setSharedVideos(capabilities.sharedVideos === true);
        setMode(capabilities.hostedAlpha ? 'alpha' : local ? 'local' : 'cloud');
        const identity = local ? null : await session();
        if (!active) return;
        setOperator(identity?.operator ?? false);
        setAuthRequired(false); setConnectionError(''); setAvailable(true);
        await poll();
      } catch (cause) {
        if (!active) return;
        setAvailable(false); setAuthRequired(cause instanceof ApiError && cause.status === 401);
        if (!(cause instanceof ApiError && cause.status === 401)) setConnectionError(cause instanceof Error ? cause.message : 'Video service unavailable.');
        reconnect();
      }
    };
    void connect();
    return () => { active = false; clearTimeout(timer); };
  }, [enabled, filter, destinations.active]);
  const update = (job: VideoJob) => setJobs(current => [job, ...current.filter(item => item.id !== job.id)]);
  const signIn = async () => {
    setSubmitting(true); setError('');
    try {
      await signInToLocalPreview();
      setAuthRequired(false); setAvailable(true);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to sign in.'); }
    finally { setSubmitting(false); void commerce?.refresh(); }
  };
  const start = async (input: VideoRequest) => {
    setSubmitting(true); setError('');
    const submit = async () => {
      if (mode === 'cloud') {
        const id = await submitCloudVideo(input);
        // Acceptance is authoritative even if the first status refresh fails.
        void cloudVideo(id).then(update).catch(() => undefined);
        return id;
      }
      const job = await createVideo(input); update(job); return job.id;
    };
    try {
      if (mode === 'cloud' && localSignIn && authRequired) {
        await signInToLocalPreview();
        setAuthRequired(false); setAvailable(true);
      }
      try { return await submit(); }
      catch (cause) {
        if (!(mode === 'cloud' && localSignIn && cause instanceof ApiError && cause.status === 401)) throw cause;
        await signInToLocalPreview();
        setAuthRequired(false); setAvailable(true);
        return await submit();
      }
    }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to start the render.'); }
    finally { setSubmitting(false); void commerce?.refresh(); }
  };
  const action = async (id: string, kind: 'cancel' | 'retry') => {
    setError('');
    try { update(await (mode === 'cloud' ? cloudAction(id, kind) : cancelVideo(id))); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'The action failed.'); }
    finally { void commerce?.refresh(); }
  };
  const loadMore = async () => {
    if (!nextCursor) return;
    try {
      const page = await cloudVideos(nextCursor, filter);
      loadedOlder.current = true;
      setJobs(current => [...current, ...page.jobs.filter(job => !current.some(item => item.id === job.id))]);
      setNextCursor(page.nextCursor);
    } catch { setError('Unable to load older videos.'); }
  };
  const edit = async (id: string, title?: string) => {
    setError('');
    try {
      const job = await apiRequest<VideoJob>(`${API}/render-jobs/${encodeURIComponent(id)}`, title === undefined ? { method: 'DELETE' } : { ...jsonPost({ title }), method: 'PATCH' });
      if (title === undefined) setJobs(current => current.filter(item => item.id !== id)); else update(job);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to update video.'); }
    finally { void commerce?.refresh(); }
  };
  if (destinations.active) return { commerce: destinations.commerce, filter, setFilter, edit: destinations.edit,
    available: destinations.available, mode: destinations.mode, socialLayoutsSupported: true, sharedVideos: false, operator: false,
    authRequired: false, localSignIn: false, signIn, jobs: destinations.jobs, nextCursor: destinations.nextCursor, loadMore: destinations.loadMore,
    error: destinations.error, submitting: destinations.submitting, start: destinations.start, cancel: destinations.cancel, retry: destinations.retry,
    busy: destinations.busy, destinations };
  return { destinations: undefined as typeof destinations | undefined, commerce, filter, setFilter, edit, available, mode, socialLayoutsSupported, sharedVideos, operator, authRequired, localSignIn, signIn, jobs, nextCursor, loadMore, error: error || connectionError, submitting, start,
    cancel: (id: string) => action(id, 'cancel'), retry: (id: string) => action(id, 'retry'),
    busy: submitting || (mode === 'local' && jobs.some(job => job.state === 'processing')) };
}
type ExportState = ReturnType<typeof useVideoExports>;
export type VideoExports = Omit<ExportState, 'destinations'> & { destinations?: ExportState['destinations'] };
